import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { jobOwnersUrl, matchesUrl, searchUrl } from 'findem-helpers';

// Type definitions
export interface User {
  uid: string;
  email: string;
  name: string;
  [key: string]: any;
}

export interface IcpData {
  ref?: string;
  mode?: string;
  cxt_data?: any;
  [key: string]: any;
}

export interface AtsConfig {
  [key: string]: any;
}

export interface OrgSettings {
  domain_to_exclude_search?: string[];
  [key: string]: any;
}

export interface AppState {
  user: User;
  currentIcp: IcpData | null;
  atsConfig: AtsConfig | null;
  allMacros: Record<string, any>;
  orgSettings: OrgSettings;
}

export interface AppContextType extends AppState {
  setUser: (user: User) => void;
  setCurrentIcp: (icp: IcpData | null) => void;
  setAtsConfig: (config: AtsConfig | null) => void;
  setAllMacros: (macros: Record<string, any>) => void;
  setOrgSettings: (settings: OrgSettings) => void;
}

const defaultUser: User = {
  uid: '',
  email: '',
  name: '',
};

const defaultState: AppState = {
  user: defaultUser,
  currentIcp: null,
  atsConfig: null,
  allMacros: [],
  orgSettings: {},
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User>(defaultUser);
  const [currentIcp, setCurrentIcpState] = useState<IcpData | null>(null);
  const [atsConfig, setAtsConfigState] = useState<AtsConfig | null>(null);
  const [allMacros, setAllMacrosState] = useState<Record<string, any>>({});
  const [orgSettings, setOrgSettingsState] = useState<OrgSettings>({});

  const setUser = useCallback((newUser: User) => {
    setUserState(newUser);
  }, []);

  const setCurrentIcp = useCallback((icp: IcpData | null) => {
    setCurrentIcpState(icp);
  }, []);

  const setAtsConfig = useCallback((config: AtsConfig | null) => {
    setAtsConfigState(config);
  }, []);

  const setAllMacros = useCallback((macros: any[]) => {
    setAllMacrosState(macros);
  }, []);

  const setOrgSettings = useCallback((settings: OrgSettings) => {
    setOrgSettingsState(settings);
  }, []);

  const value: AppContextType = {
    user,
    currentIcp,
    atsConfig,
    allMacros,
    orgSettings,
    setUser,
    setCurrentIcp,
    setAtsConfig,
    setAllMacros,
    setOrgSettings,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Hook aliases for compatibility with existing code
export const useSelector = (selector: (state: any) => any): any => {
  const context = useAppContext();

  // Map context to Redux-like state structure for compatibility
  const state = {
    login: {
      user: context.user,
    },
    icps: {
      edit_icp: context.currentIcp,
    },
    ats: {
      config: context.atsConfig,
    },
    attrs_state: {
      all_macros: context.allMacros,
    },
    analytics: {
      org_settings: context.orgSettings,
    },
  };

  return selector(state);
};

export const useDispatch = () => {
  const context = useAppContext();

  return async (action: any) => {
    // Handle action dispatches
    switch (action.type) {
      case 'GET_MACROS_REQUEST':
        try {
          const joid = action.payload.id;
          const ignoreNonLeadershipTitles = action.payload.ignore_non_leadership_titles || false;

          // First fetch public queries to get the timestamp
          const pubQueriesUrl = `${searchUrl}/pub/api/fetch_pub_queries?joid=${joid}${ignoreNonLeadershipTitles ? '&ignore_non_leadership_titles=true' : ''}`;
          const pubQueriesResult = await axios.get(pubQueriesUrl, { withCredentials: true });

          const mergedResults: Record<string, any> = {};

          // Process public queries response
          if (pubQueriesResult.data?.public_macros) {
            Object.assign(mergedResults, pubQueriesResult.data.public_macros);
          }

          // Extract timestamp for cache invalidation
          const fromTs = pubQueriesResult.data?.ts;

          // Then fetch private updated queries using the timestamp from public queries
          if (fromTs) {
            const pvtQueriesUrl = `${searchUrl}/pub/api/fetch_pvt_updated_queries?joid=${joid}&from_ts=${fromTs}${ignoreNonLeadershipTitles ? '&ignore_non_leadership_titles=true' : ''}`;
            const pvtQueriesResult = await axios.get(pvtQueriesUrl, { withCredentials: true });

            // Process private updated queries response
            if (pvtQueriesResult.data) {
              const privateData = pvtQueriesResult.data;

              // First, remove deleted macros
              if (privateData.deleted && privateData.deleted.length > 0) {
                privateData.deleted.forEach((item: any) => {
                  if (mergedResults[item.macro_id]) {
                    delete mergedResults[item.macro_id];
                  }
                });
              }

              // Then add created/updated macros
              if (privateData.created) {
                Object.assign(mergedResults, privateData.created);
              }
            }
          }

          context.setAllMacros(mergedResults);
        } catch (error) {
          console.error('GET_MACROS_REQUEST error:', error);
        }
        break;
      case 'GET_ORG_SETTINGS_REQUEST':
        try {
          const response = await axios.get(`${jobOwnersUrl}/hm/api/org/prefs`, {
            params: { joid: action.payload.joid },
            withCredentials: true
          });
          context.setOrgSettings(response.data);
        } catch (error) {
          console.error('GET_ORG_SETTINGS_REQUEST error:', error);
        }
        break;
      case 'GET_ICP_BY_ID_REQUEST':
        try {
          const response = await axios.post(`${searchUrl}/pub/api/icp`, {
            type: 'ByIcpId',
            icpid: action.payload.icpid,
            joid: action.payload.joid
          }, {
            withCredentials: true
          });
          context.setCurrentIcp(response.data);
        } catch (error) {
          console.error('GET_ICP_BY_ID_REQUEST error:', error);
        }
        break;
      case 'GET_ATS_CONFIG_REQUEST':
        try {
          const response = await axios.get(`${matchesUrl}/hm/api/ats-config`, {
            params: { joid: action.payload.joid },
            withCredentials: true
          });
          context.setAtsConfig(response.data);
        } catch (error) {
          console.error('GET_ATS_CONFIG_REQUEST error:', error);
        }
        break;
      case 'SET_USER':
        context.setUser(action.payload);
        break;
      case 'SET_CURRENT_ICP':
        context.setCurrentIcp(action.payload);
        break;
      case 'SET_ATS_CONFIG':
        context.setAtsConfig(action.payload);
        break;
      case 'SET_ALL_MACROS':
        context.setAllMacros(action.payload);
        break;
      case 'SET_ORG_SETTINGS':
        context.setOrgSettings(action.payload);
        break;
      default:
        console.warn('Unknown action type:', action.type);
    }
  };
};
